import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Map, Database, ChevronRight, ChevronLeft, X } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: '[data-tour="chat-panel"]',
    title: 'Chat Panel',
    content: <>Ask questions about your data here. The <strong className="text-gray-900 dark:text-white font-semibold">AI agent</strong> can <strong className="text-gray-900 dark:text-white font-semibold">query datasets</strong>, <strong className="text-gray-900 dark:text-white font-semibold">generate charts</strong>, and <strong className="text-gray-900 dark:text-white font-semibold">create map layers</strong>.</>,
    placement: 'right',
    icon: MessageSquare,
  },
  {
    target: '[data-tour="map-panel"]',
    title: 'Map Panel',
    content: <>Visualise <strong className="text-gray-900 dark:text-white font-semibold">spatial data</strong> on an interactive map. Layers are generated from your <strong className="text-gray-900 dark:text-white font-semibold">chat queries</strong> or the <strong className="text-gray-900 dark:text-white font-semibold">layer builder</strong>.</>,
    placement: 'left',
    icon: Map,
  },
  {
    target: '[data-tour="data-panel"]',
    title: 'Data Panel',
    content: <><strong className="text-gray-900 dark:text-white font-semibold">Explore and manage</strong> your <strong className="text-gray-900 dark:text-white font-semibold">datasets</strong> and <strong className="text-gray-900 dark:text-white font-semibold">functions</strong> or import new ones.</>,
    placement: 'top',
    icon: Database,
  },
];

const ARROW_SIZE = 10;
const TOOLTIP_GAP = 16;

/**
 * Compute tooltip position relative to the target rect.
 */
function computePosition(rect, placement, tooltipWidth, tooltipHeight) {
  let top = 0;
  let left = 0;
  let arrowTop = '';
  let arrowLeft = '';
  let arrowTransform = '';

  switch (placement) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowTop = `-${ARROW_SIZE}px`;
      arrowLeft = '50%';
      arrowTransform = 'translateX(-50%)';
      break;
    case 'top':
      top = rect.top - tooltipHeight - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowTop = `${tooltipHeight}px`;
      arrowLeft = '50%';
      arrowTransform = 'translateX(-50%) rotate(180deg)';
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - TOOLTIP_GAP;
      arrowTop = '50%';
      arrowLeft = `${tooltipWidth}px`;
      arrowTransform = 'translateY(-50%) rotate(-90deg)';
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + TOOLTIP_GAP;
      arrowTop = '50%';
      arrowLeft = `-${ARROW_SIZE}px`;
      arrowTransform = 'translateY(-50%) rotate(90deg)';
      break;
    default:
      break;
  }

  // Clamp to viewport edges
  const pad = 16;
  if (left < pad) left = pad;
  if (left + tooltipWidth > window.innerWidth - pad) left = window.innerWidth - tooltipWidth - pad;
  if (top < pad) top = pad;
  if (top + tooltipHeight > window.innerHeight - pad) top = window.innerHeight - tooltipHeight - pad;

  return {
    top,
    left,
    arrowStyle: { top: arrowTop, left: arrowLeft, transform: arrowTransform },
  };
}

const GuidedTour = ({ run, onFinish }) => {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  // Reset step when tour starts
  useEffect(() => {
    if (run) setStep(0);
  }, [run]);

  // Measure the target element on each step
  const measureTarget = useCallback(() => {
    if (!run) return;
    const currentStep = TOUR_STEPS[step];
    if (!currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    }
  }, [run, step]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget]);

  // Measure the tooltip once rendered
  useEffect(() => {
    if (tooltipRef.current) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      setTooltipSize({ width: offsetWidth, height: offsetHeight });
    }
  }, [step, run, targetRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!run) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        localStorage.setItem('manteo_tour_completed', 'true');
        onFinish();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (step < TOUR_STEPS.length - 1) setStep(s => s + 1);
        else {
          localStorage.setItem('manteo_tour_completed', 'true');
          onFinish();
        }
      } else if (e.key === 'ArrowLeft') {
        if (step > 0) setStep(s => s - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [run, step, onFinish]);

  if (!run || !targetRect) return null;

  const currentStep = TOUR_STEPS[step];
  const StepIcon = currentStep.icon;
  const isLastStep = step === TOUR_STEPS.length - 1;

  // Spotlight cutout with padding
  const spotPad = 10;
  const spotRect = {
    top: targetRect.top - spotPad,
    left: targetRect.left - spotPad,
    width: targetRect.width + spotPad * 2,
    height: targetRect.height + spotPad * 2,
    borderRadius: 16,
  };

  const pos = computePosition(
    targetRect,
    currentStep.placement,
    tooltipSize.width || 400,
    tooltipSize.height || 240
  );

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }}>
      {/* Overlay with spotlight cutout */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotRect.left}
              y={spotRect.top}
              width={spotRect.width}
              height={spotRect.height}
              rx={spotRect.borderRadius}
              ry={spotRect.borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.5)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring glow */}
      <div
        style={{
          position: 'absolute',
          top: spotRect.top - 2,
          left: spotRect.left - 2,
          width: spotRect.width + 4,
          height: spotRect.height + 4,
          borderRadius: spotRect.borderRadius + 2,
          border: '2px solid rgba(16, 185, 129, 0.5)',
          boxShadow: '0 0 20px rgba(16, 185, 129, 0.15), inset 0 0 20px rgba(16, 185, 129, 0.05)',
          pointerEvents: 'none',
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-700/80 rounded-3xl overflow-hidden"
        style={{
          position: 'absolute',
          top: pos.top,
          left: pos.left,
          width: 400,
          zIndex: 10001,
          animation: 'tourFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Arrow */}
        <div
          style={{
            position: 'absolute',
            ...pos.arrowStyle,
            width: 0,
            height: 0,
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid var(--tour-arrow-color, white)`,
            zIndex: 10002,
          }}
          className="[--tour-arrow-color:white] dark:[--tour-arrow-color:#111827]"
        />

        {/* Close button */}
        <button
          onClick={() => {
            localStorage.setItem('manteo_tour_completed', 'true');
            onFinish();
          }}
          className="absolute top-4 right-4 p-1.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all z-10"
        >
          <X size={16} />
        </button>

        {/* Content */}
        <div className="px-7 pt-7 pb-5">
          {/* Icon + Title */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800/50 flex items-center justify-center shrink-0">
              <StepIcon size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-base font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none">
                {currentStep.title}
              </h3>
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                {step + 1} of {TOUR_STEPS.length}
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {currentStep.content}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-7 py-4 bg-gray-50/80 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800">
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? 'w-6 bg-emerald-500'
                    : i < step
                    ? 'w-3 bg-emerald-300 dark:bg-emerald-700'
                    : 'w-3 bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-gray-700 transition-all"
              >
                <ChevronLeft size={14} />
                Back
              </button>
            )}
            <button
              onClick={() => {
                if (isLastStep) {
                  localStorage.setItem('manteo_tour_completed', 'true');
                  onFinish();
                } else {
                  setStep(s => s + 1);
                }
              }}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all shadow-sm shadow-emerald-200 dark:shadow-emerald-900/30"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>

      {/* Inline keyframe animation */}
      <style>{`
        @keyframes tourFadeIn {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default GuidedTour;
